import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { clubFeedbackSchema } from "@sportscanner/schemas";
import { Alert, AlertDescription, Badge, Button } from "@sportscanner/ui";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import { Row, Section } from "@/components/settings-form";
import ClubScopeShell from "@/components/club-scope-shell";

interface ReviewRow {
  id: string;
  rating: number;
  comment: string | null;
  playerName: string | null;
  source: "WEB" | "EMAIL";
  submittedAt: string;
  booking: {
    id: string;
    date: string;
    startTime: string;
    playerEmail: string | null;
    playerPhone: string | null;
  } | null;
}

interface ReviewsResponse {
  reviews: ReviewRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  averageRating: number | null;
  ratingCount: number;
}

function StarDisplay({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-4 h-4 ${
            n <= rating
              ? "fill-amber-400 text-amber-400"
              : "fill-neutral-200 text-neutral-200 dark:fill-neutral-600 dark:text-neutral-600"
          }`}
        />
      ))}
    </div>
  );
}

interface FeedbackSettingsData {
  feedbackEnabled: boolean;
  feedbackViaEmail: boolean;
  feedbackViaWeb: boolean;
}

// Ratings collection settings — the master switch plus its two delivery
// channels. Lives here (not on the Access page) so managers flip it on right
// where they read the ratings it produces. Saves via the dedicated
// /clubs/:id/feedback endpoint; channels collapse when the switch is off,
// mirroring the server clamp.
function FeedbackSettings({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const { data: club } = useQuery({
    queryKey: ["admin", "clubs", clubId, "feedback"],
    queryFn: async () => {
      const res = await apiGet<{ club: FeedbackSettingsData }>(
        `/api/admin/clubs/${clubId}`,
      );
      return res.club;
    },
  });

  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [feedbackViaEmail, setFeedbackViaEmail] = useState(false);
  const [feedbackViaWeb, setFeedbackViaWeb] = useState(false);

  useEffect(() => {
    if (!club) return;
    setFeedbackEnabled(club.feedbackEnabled);
    setFeedbackViaEmail(club.feedbackViaEmail);
    setFeedbackViaWeb(club.feedbackViaWeb);
    setErrorCode(null);
  }, [club]);

  // Mirror the server clamp: channels are only meaningful with the switch on.
  const eff = useMemo(
    () => ({
      feedbackEnabled,
      feedbackViaEmail: feedbackEnabled && feedbackViaEmail,
      feedbackViaWeb: feedbackEnabled && feedbackViaWeb,
    }),
    [feedbackEnabled, feedbackViaEmail, feedbackViaWeb],
  );

  const isDirty = useMemo(() => {
    if (!club) return false;
    return (
      eff.feedbackEnabled !== club.feedbackEnabled ||
      eff.feedbackViaEmail !== club.feedbackViaEmail ||
      eff.feedbackViaWeb !== club.feedbackViaWeb
    );
  }, [club, eff]);

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = clubFeedbackSchema.safeParse(eff);
      if (!parsed.success) {
        throw {
          error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut(`/api/admin/clubs/${clubId}/feedback`, parsed.data);
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("reviewsPage.settings.savedToast"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "clubs", clubId, "feedback"],
      });
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <Section title={t("reviewsPage.settings.title")}>
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}
      <Row
        id="feedback-enabled"
        label={t("reviewsPage.settings.enabled.label")}
        hint={t("reviewsPage.settings.enabled.hint")}
        checked={feedbackEnabled}
        onCheckedChange={(v) => {
          setFeedbackEnabled(v);
          if (!v) {
            setFeedbackViaEmail(false);
            setFeedbackViaWeb(false);
          }
        }}
        disabled={mutation.isPending}
      />
      {feedbackEnabled && (
        <div className="pl-4 border-l-2 border-neutral-100 space-y-4">
          <Row
            id="feedback-via-web"
            label={t("reviewsPage.settings.viaWeb.label")}
            hint={t("reviewsPage.settings.viaWeb.hint")}
            checked={feedbackViaWeb}
            onCheckedChange={setFeedbackViaWeb}
            disabled={mutation.isPending}
          />
          <Row
            id="feedback-via-email"
            label={t("reviewsPage.settings.viaEmail.label")}
            hint={t("reviewsPage.settings.viaEmail.hint")}
            checked={feedbackViaEmail}
            onCheckedChange={setFeedbackViaEmail}
            disabled={mutation.isPending}
          />
        </div>
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!isDirty || mutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </Section>
  );
}

function ReviewsContent({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<ReviewsResponse>({
    queryKey: ["admin-reviews", clubId, page],
    queryFn: () =>
      apiGet<ReviewsResponse>(
        `/api/admin/clubs/${clubId}/reviews?page=${page}`,
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-neutral-800 dark:text-neutral-100">
          {t("reviewsPage.title")}
        </h1>
        {data && data.ratingCount > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-2">
            <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
            <span className="text-lg font-bold text-neutral-800 dark:text-neutral-100">
              {data.averageRating?.toFixed(1)}
            </span>
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              ({data.ratingCount})
            </span>
          </div>
        )}
      </div>

      <FeedbackSettings clubId={clubId} />

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 bg-neutral-100 dark:bg-neutral-800 rounded-xl animate-pulse"
            />
          ))}
        </div>
      )}

      {!isLoading && data && data.reviews.length === 0 && (
        <div className="text-center py-16 text-neutral-400 dark:text-neutral-500">
          <Star className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{t("reviewsPage.empty")}</p>
        </div>
      )}

      {!isLoading && data && data.reviews.length > 0 && (
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <div
              key={r.id}
              className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-4 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <StarDisplay rating={r.rating} />
                  <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                    {r.playerName ??
                      r.booking?.playerEmail ??
                      t("reviewsPage.anonymous")}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <Badge variant="outline" className="text-xs">
                    {r.source === "WEB"
                      ? t("reviewsPage.sourceWeb")
                      : t("reviewsPage.sourceEmail")}
                  </Badge>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500">
                    {r.booking?.date ?? ""} {r.booking?.startTime ?? ""}
                  </p>
                </div>
              </div>
              {r.comment && (
                <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {r.comment}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t("common.prev")}
          </Button>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {t("common.pageOf", { page: data.page, total: data.totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="cursor-pointer"
          >
            {t("common.next")}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ReviewsPage() {
  return (
    <ClubScopeShell>
      {(clubId) => <ReviewsContent clubId={clubId} />}
    </ClubScopeShell>
  );
}
